import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setCurrentSubStep } from '../store/rfqSlice';
import { RFQ } from '../types';
import { bomClient } from '../client/bomClient';
import { componentKeyOf } from '../components/discovery/discoveryModel';
import { useActionError } from './useActionError';
import type { BomAnalysisRun } from './useBomAnalysisRun';

/**
 * Step 3c writes: starting supplier research (through the Smart BOM
 * checkpoint), retrying one part, switching a line to an alternative spec,
 * and the Excel export. Failures surface in
 * Robbie's voice — the start inline in the checkpoint, the rest as a toast.
 */
export const useDiscoveryActions = (rfq: RFQ, run: BomAnalysisRun) => {
  const dispatch = useDispatch();
  const actionError = useActionError();
  const { activeBomIdRef, components, analysisData, rfqData, startPolling } = run;
  const [isExporting, setIsExporting] = useState(false);
  const [retryingComponents, setRetryingComponents] = useState<Record<string, boolean>>({});
  const [isStartingResearch, setIsStartingResearch] = useState(false);
  // `${componentKey}|${specGroup}` of the switch in flight, so only that button spins.
  const [swappingSpec, setSwappingSpec] = useState<string | null>(null);
  // Continue-from-validation runs through the Smart BOM checkpoint first:
  // research fires against the BOM as it stands (target prices included), so
  // the buyer is offered the smarter BOM BEFORE it starts.
  const [checkpointOpen, setCheckpointOpen] = useState(false);
  // A failed start reopens the checkpoint with the reason inline.
  const [researchStartError, setResearchStartError] = useState<string | null>(null);

  const exportToExcel = async () => {
    if (!activeBomIdRef.current) return;
    setIsExporting(true);
    try {
      await bomClient.exportToExcel(activeBomIdRef.current);
    } catch {
      actionError.show("I couldn't export your BOM", 'Nothing changed. Try the export again.');
    } finally {
      setIsExporting(false);
    }
  };

  const retryComponent = async (componentId: string) => {
    const bomId = activeBomIdRef.current;
    if (!bomId) return;
    // Same key derivation as SupplierDiscovery/ComponentListPane — components
    // from the API carry `_id`, not `id`.
    const index = components.findIndex((c: object, idx: number) => componentKeyOf(c, idx) === componentId);
    if (index === -1) return;
    setRetryingComponents((prev) => ({ ...prev, [componentId]: true }));
    try {
      await bomClient.retryComponentResearch(bomId, index.toString());
      startPolling(bomId);
    } catch {
      actionError.show("I couldn't restart that part", 'Its research is unchanged. Try again in a moment.');
    } finally {
      setRetryingComponents((prev) => ({ ...prev, [componentId]: false }));
    }
  };

  /** "Use this instead": the line takes the alternative's spec; the read then shows it as the main list. */
  const swapSpec = async (componentKey: string, specGroup: string) => {
    const bomId = activeBomIdRef.current;
    if (!bomId || swappingSpec) return;
    setSwappingSpec(`${componentKey}|${specGroup}`);
    try {
      await bomClient.swapComponentSpec(bomId, componentKey, specGroup);
      startPolling(bomId);
    } catch (error) {
      const sentence = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      actionError.show("I couldn't switch that line's spec", sentence || 'The line keeps its spec. Try again in a moment.');
    } finally {
      setSwappingSpec(null);
    }
  };

  const startResearch = async () => {
    const bomId = activeBomIdRef.current;
    if (!bomId) return;
    setIsStartingResearch(true);
    setResearchStartError(null);
    try {
      await bomClient.startSupplierResearch(bomId, {
        components,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- requirements is not on the RFQ prop type
        requirements: rfqData?.requirements || (rfq as any)?.requirements || {},
        contextAnswers: analysisData?.contextAnswers || {},
        ruleResults: analysisData?.ruleResults || [],
        aiSuggestion: analysisData?.aiSuggestion || null,
      });
      dispatch(setCurrentSubStep(3));
      startPolling(bomId);
    } catch (error) {
      const sentence = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setResearchStartError(sentence || 'Nothing was queued.');
      setCheckpointOpen(true);
    } finally {
      setIsStartingResearch(false);
    }
  };

  return {
    actionError,
    isExporting,
    retryingComponents,
    isStartingResearch,
    exportToExcel,
    retryComponent,
    swapSpec,
    swappingSpec,
    checkpoint: {
      open: checkpointOpen,
      error: researchStartError,
      show: () => setCheckpointOpen(true),
      cancel: () => {
        setCheckpointOpen(false);
        setResearchStartError(null);
      },
      proceed: () => {
        setCheckpointOpen(false);
        void startResearch();
      },
    },
  };
};

export default useDiscoveryActions;
